import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ChevronRight,
  Loader2,
  Scale,
  FileSearch,
  CheckCircle2,
  XCircle,
  Download,
  Eye,
  RefreshCw,
  X,
  ShieldCheck,
  History,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminListLabourAnalyses,
  adminUpdateLabourAnalysis,
  adminGetLabourAnalysis,
  adminRerunLabourAnalysis,
  exportLabourAnalysisPdf,
} from "@/lib/api/labour-analyzer.functions";
import { downloadBase64File } from "@/lib/download-base64";

export const Route = createFileRoute("/_authenticated/portal/admin/labour-analyses")({
  head: () => ({
    meta: [{ title: "Admin · Labour analyses" }, { name: "robots", content: "noindex" }],
  }),
  component: AdminLabourList,
});

const STATUSES = ["all", "analyzing", "admin_review", "approved", "rejected", "error", "archived"];

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { l: string; c: string }> = {
    analyzing: { l: "Analysing", c: "bg-amber-100 text-amber-900" },
    admin_review: { l: "Needs review", c: "bg-sky-100 text-sky-900" },
    approved: { l: "Approved", c: "bg-emerald-100 text-emerald-900" },
    rejected: { l: "Rejected", c: "bg-rose-100 text-rose-900" },
    error: { l: "Error", c: "bg-rose-100 text-rose-900" },
    archived: { l: "Archived", c: "bg-muted text-muted-foreground" },
  };
  const m = map[status] ?? { l: status, c: "bg-muted text-muted-foreground" };
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.c}`}>{m.l}</span>
  );
}

function AdminLabourList() {
  const qc = useQueryClient();
  const fetchAll = useServerFn(adminListLabourAnalyses);
  const update = useServerFn(adminUpdateLabourAnalysis);
  const exportPdf = useServerFn(exportLabourAnalysisPdf);
  const rerun = useServerFn(adminRerunLabourAnalysis);

  const q = useQuery({
    queryKey: ["admin-labour-analyses"],
    queryFn: () => fetchAll(),
    refetchInterval: 15000,
  });
  const [filter, setFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<Record<string, string | null>>({});
  const [previewId, setPreviewId] = useState<string | null>(null);

  const rows = (q.data?.rows ?? []).filter((r: any) => {
    if (filter !== "all" && r.status !== filter) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      return (
        r.matter_title?.toLowerCase().includes(s) ||
        r.client_name?.toLowerCase().includes(s) ||
        r.student_number?.toLowerCase().includes(s) ||
        r.ai_summary?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  async function downloadPdf(id: string) {
    setBusy((b) => ({ ...b, [id]: "pdf" }));
    try {
      const { base64, filename } = await exportPdf({ data: { id } });
      downloadBase64File(base64, filename);
      toast.success("Secure PDF downloaded");
    } catch (e: any) {
      toast.error(e?.message ?? "PDF export failed");
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  }

  async function regenerate(id: string) {
    setBusy((b) => ({ ...b, [id]: "regen" }));
    try {
      await rerun({ data: { id } });
      toast.success("Analysis re-run complete");
      qc.invalidateQueries({ queryKey: ["admin-labour-analyses"] });
      qc.invalidateQueries({ queryKey: ["admin-labour-analysis", id] });
    } catch (e: any) {
      toast.error(e?.message ?? "Re-run failed");
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  }

  return (
    <div className="space-y-6">
      <header className="rounded-2xl border border-border bg-gradient-to-br from-navy/[0.04] to-gold/[0.06] p-6 shadow-soft">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold">
          <Scale className="h-4 w-4" /> Admin · Labour Analyses
        </div>
        <h1 className="font-display text-2xl text-navy">AI Labour-Law Matters</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Preview the original document and the AI-analysed report, then approve or reject with a
          decision note. Approved reports become downloadable to the client via short-lived
          signed URLs.
        </p>
        <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-900">
          <ShieldCheck className="h-3 w-3" /> Every download is authorised per request
        </p>
        <Link
          to={"/portal/admin/labour-analyses-audit" as any}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-navy hover:underline"
        >
          <History className="h-3.5 w-3.5" /> Approval audit trail
        </Link>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-full border px-3 py-1 text-xs ${
                filter === s ? "border-navy bg-navy text-navy-foreground" : "border-input bg-background"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search matter, client, summary…"
          className="ml-auto min-w-[220px] rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        />
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          No matters match.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r: any) => {
            const b = busy[r.id];
            const hasReport = !!r.ai_summary || r.status === "approved" || r.status === "admin_review" || r.status === "rejected";
            return (
              <li
                key={r.id}
                className="rounded-lg border border-border bg-card p-4 hover:border-navy"
              >
                <div className="flex items-start justify-between gap-3">
                  <Link
                    to={"/portal/admin/labour-analyses/$id" as any}
                    params={{ id: r.id } as any}
                    className="min-w-0 flex-1"
                  >
                    <p className="truncate text-sm font-semibold text-navy">{r.matter_title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {r.client_name} · {r.student_number ?? "—"} ·{" "}
                      {new Date(r.created_at).toLocaleString("en-ZA")} ·{" "}
                      {r.doc_type.replaceAll("_", " ")}
                    </p>
                    {r.ai_summary && (
                      <p className="mt-1 line-clamp-2 text-xs text-foreground/80">{r.ai_summary}</p>
                    )}
                  </Link>
                  <div className="flex shrink-0 items-center gap-2">
                    {typeof r.ai_confidence === "number" && (
                      <span className="text-[10px] text-muted-foreground">{r.ai_confidence}%</span>
                    )}
                    <StatusBadge status={r.status} />
                    <Link
                      to={"/portal/admin/labour-analyses/$id" as any}
                      params={{ id: r.id } as any}
                      className="text-muted-foreground hover:text-navy"
                      aria-label="Open matter"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2 border-t border-border/60 pt-3">
                  <button
                    type="button"
                    onClick={() => setPreviewId(r.id)}
                    disabled={!hasReport}
                    className="inline-flex items-center gap-1 rounded-md border border-navy bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Preview AI report & original document, then approve or reject"
                  >
                    <Eye className="h-3.5 w-3.5" /> Preview & review
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadPdf(r.id)}
                    disabled={!hasReport || b === "pdf"}
                    className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-semibold text-navy hover:border-navy disabled:cursor-not-allowed disabled:opacity-50"
                    title="Download the analysed AI report as a branded PDF"
                  >
                    {b === "pdf" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Download AI report PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => regenerate(r.id)}
                    disabled={b === "regen" || r.status === "analyzing"}
                    className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-xs text-navy hover:border-navy disabled:cursor-not-allowed disabled:opacity-50"
                    title="Re-run the AI analysis on the original document"
                  >
                    {b === "regen" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                    Regenerate
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <FileSearch className="h-3 w-3" /> {rows.length} matter(s) shown
      </p>

      {previewId && (
        <ReviewModal
          id={previewId}
          onClose={() => setPreviewId(null)}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ["admin-labour-analyses"] });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review modal: signed-URL preview of original + AI report + approve/reject UI
// ---------------------------------------------------------------------------

function ReviewModal({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const fetchOne = useServerFn(adminGetLabourAnalysis);
  const update = useServerFn(adminUpdateLabourAnalysis);
  const exportPdf = useServerFn(exportLabourAnalysisPdf);

  const q = useQuery({
    queryKey: ["admin-labour-analysis", id],
    queryFn: () => fetchOne({ data: { id } }),
  });

  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  async function decide(status: "approved" | "rejected") {
    setBusy(status);
    try {
      await update({ data: { id, status, decisionNotes: notes || null } });
      toast.success(
        status === "approved"
          ? "Approved — client can now securely download the report"
          : "Rejected — recorded with your note",
      );
      qc.invalidateQueries({ queryKey: ["admin-labour-analysis", id] });
      onChanged();
    } catch (e: any) {
      toast.error(e?.message ?? "Action failed");
    } finally {
      setBusy(null);
    }
  }

  async function downloadPdf() {
    setBusy("pdf");
    try {
      const { base64, filename } = await exportPdf({ data: { id } });
      downloadBase64File(base64, filename);
      toast.success("Secure PDF downloaded");
    } catch (e: any) {
      toast.error(e?.message ?? "PDF export failed");
    } finally {
      setBusy(null);
    }
  }

  const row = q.data?.row;
  const report: any = row?.ai_report ?? {};
  const auditLog: any[] = q.data?.audit ?? [];
  const decisions = auditLog.filter((a) => String(a.action).startsWith("decision_"));

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 p-2 sm:p-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative flex w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-card shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-border bg-gradient-to-br from-navy/[0.06] to-gold/[0.08] px-5 py-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gold">
              Admin review · Labour-law matter
            </p>
            <h2 className="truncate font-display text-lg text-navy">
              {row?.matter_title ?? "Loading…"}
            </h2>
            {row && (
              <p className="text-[11px] text-muted-foreground">
                {row.original_filename} · {String(row.doc_type).replaceAll("_", " ")} ·{" "}
                {new Date(row.created_at).toLocaleString("en-ZA")}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-navy"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {q.isLoading || !row ? (
          <div className="grid flex-1 place-items-center p-10 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="grid flex-1 gap-0 overflow-hidden lg:grid-cols-2">
            {/* Left: original document preview via short-lived signed URL */}
            <div className="flex min-h-[420px] flex-col border-b border-border bg-muted/30 lg:border-b-0 lg:border-r">
              <div className="flex items-center justify-between border-b border-border bg-card/60 px-4 py-2 text-[11px] font-semibold text-navy">
                <span>Original uploaded document (secure short-lived link)</span>
                {q.data?.downloadUrl && (
                  <a
                    href={q.data.downloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded border border-input bg-background px-2 py-0.5 text-[10px] text-navy hover:border-navy"
                  >
                    <Download className="h-3 w-3" /> Open
                  </a>
                )}
              </div>
              {q.data?.downloadUrl ? (
                <iframe
                  title="Original document preview"
                  src={q.data.downloadUrl}
                  className="h-full w-full flex-1 bg-white"
                />
              ) : (
                <p className="p-6 text-xs text-muted-foreground">
                  Could not generate a secure preview URL.
                </p>
              )}
            </div>

            {/* Right: AI report + decision panel + audit trail */}
            <div className="flex max-h-[80vh] flex-col overflow-y-auto">
              <div className="space-y-4 p-5">
                <section>
                  <h3 className="text-sm font-semibold text-navy">AI summary</h3>
                  <p className="mt-1 whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs text-foreground/90">
                    {row.admin_edits?.summary ?? row.ai_summary ?? "(no summary)"}
                  </p>
                </section>

                {Array.isArray(report.key_facts) && report.key_facts.length > 0 && (
                  <section>
                    <h3 className="text-sm font-semibold text-navy">Key facts</h3>
                    <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                      {report.key_facts.map((k: string, i: number) => (
                        <li key={i}>{k}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {Array.isArray(report.next_steps) && report.next_steps.length > 0 && (
                  <section>
                    <h3 className="text-sm font-semibold text-navy">Recommended next steps</h3>
                    <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                      {report.next_steps.map((s: any, i: number) => (
                        <li key={i}>
                          {s.step}
                          {s.deadline_days != null ? ` (within ${s.deadline_days} days)` : ""}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <section className="rounded-lg border border-border bg-muted/20 p-3">
                  <h3 className="text-sm font-semibold text-navy">Decision</h3>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Add a decision note (recorded in the audit trail), then approve or reject.
                  </p>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    maxLength={4000}
                    placeholder="e.g. Approved — facts align with the dismissal record. Forward to client."
                    className="mt-2 w-full rounded-md border border-input bg-background p-2 text-xs"
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => decide("approved")}
                      disabled={busy !== null}
                      className="inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700/90 disabled:opacity-60"
                    >
                      {busy === "approved" ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      )}
                      Approve & release
                    </button>
                    <button
                      type="button"
                      onClick={() => decide("rejected")}
                      disabled={busy !== null}
                      className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-background px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                    >
                      {busy === "rejected" ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5" />
                      )}
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={downloadPdf}
                      disabled={busy !== null}
                      className="ml-auto inline-flex items-center gap-1 rounded-md border border-navy bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
                    >
                      {busy === "pdf" ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Download className="h-3.5 w-3.5" />
                      )}
                      Download AI report PDF
                    </button>
                  </div>
                </section>

                <section>
                  <h3 className="text-sm font-semibold text-navy">Approval audit trail</h3>
                  {decisions.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground">
                      No approval decisions yet. The next action is logged here.
                    </p>
                  ) : (
                    <ul className="mt-1 space-y-1 text-[11px]">
                      {decisions.map((a) => {
                        const verdict = String(a.action).replace("decision_", "");
                        return (
                          <li
                            key={a.id}
                            className="rounded border border-border bg-muted/20 px-2 py-1"
                          >
                            <strong
                              className={
                                verdict === "approved" ? "text-emerald-700" : "text-rose-700"
                              }
                            >
                              {verdict.toUpperCase()}
                            </strong>{" "}
                            by {a.details?.actor_name ?? "admin"} ·{" "}
                            <span className="font-mono text-[10px] text-muted-foreground">
                              {new Date(a.created_at).toLocaleString("en-ZA")}
                            </span>
                            {a.details?.decision_notes && (
                              <p className="mt-0.5 text-foreground/80">
                                “{a.details.decision_notes}”
                              </p>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
